import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Home', href: '/', type: 'route' },
    { name: 'About', href: '#about', type: 'anchor' },
    { name: 'Services', href: '#services', type: 'anchor' },
    { name: 'Locations', href: '#locations', type: 'anchor' },
    { name: 'Insights', href: '/insights', type: 'route' },
    { name: 'Contact', href: '/contact', type: 'route' },
  ];

  const handleNavClick = (e, item) => {
    if (item.type === 'anchor') {
      e.preventDefault();
      
      // If not on home page, navigate to home first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.querySelector(item.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      setMobileMenuOpen(false);
    }
  };

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isActive = (href, type) => {
    if (type === 'anchor') return false;
    return location.pathname === href;
  };

  return (
    <div className="min-h-screen bg-background font-manrope">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-primary/95 backdrop-blur-sm border-b border-secondary/20">
        <nav className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/venara-logo.png" 
                alt="Venara Estate" 
                className="h-14 w-auto md:h-16"
              />
              <div className="flex flex-col">
                <span className="text-secondary font-playfair text-xl md:text-2xl font-bold tracking-tight leading-tight">
                  VENARA
                </span>
                <span className="text-secondary font-playfair text-xl md:text-xl font-bold tracking-widest -mt-1">
                  ESTATE
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                item.type === 'anchor' ? (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className="text-sm font-medium tracking-wide uppercase transition-colors duration-300 cursor-pointer text-primary-foreground hover:text-secondary"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className={`text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${
                      isActive(item.href, item.type)
                        ? 'text-secondary'
                        : 'text-primary-foreground hover:text-secondary'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <a
                href="https://wa.me/919820345994"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta"
                className="bg-secondary text-secondary-foreground hover:bg-[#C5A028] px-6 py-3 font-manrope font-bold tracking-wide uppercase text-xs transition-all duration-300"
              >
                Schedule Visit
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="lg:hidden text-primary-foreground"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-primary border-t border-secondary/20"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-4 space-y-4">
                {navigation.map((item) => (
                  item.type === 'anchor' ? (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item)}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                      className="block text-sm font-medium tracking-wide uppercase transition-colors duration-300 cursor-pointer text-primary-foreground hover:text-secondary"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                      className={`block text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${
                        isActive(item.href, item.type)
                          ? 'text-secondary'
                          : 'text-primary-foreground hover:text-secondary'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )
                ))}
                <a
                  href="https://wa.me/919876543210"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-cta"
                  className="block bg-secondary text-secondary-foreground text-center px-6 py-3 font-bold tracking-wide uppercase text-xs"
                >
                  Schedule Visit
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="pt-16">{children}</main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="px-6 md:px-12 lg:px-24 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <img 
                src="/venara-logo.png" 
                alt="Venara Estate" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-primary-foreground/70 text-sm leading-relaxed">
                Curating exceptional living experiences for discerning individuals seeking luxury and distinction.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-playfair text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigation.slice(0, 4).map((item) => (
                  <li key={item.name}>
                    {item.type === 'anchor' ? (
                      <a
                        href={item.href}
                        onClick={(e) => handleNavClick(e, item)}
                        data-testid={`footer-${item.name.toLowerCase()}`}
                        className="text-primary-foreground/70 hover:text-secondary text-sm transition-colors duration-300 cursor-pointer"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        data-testid={`footer-${item.name.toLowerCase()}`}
                        className="text-primary-foreground/70 hover:text-secondary text-sm transition-colors duration-300"
                      >
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Locations */}
            <div>
              <h3 className="font-playfair text-lg font-semibold mb-4">Locations</h3>
              <ul className="space-y-2 text-primary-foreground/70 text-sm">
                <li>Mumbai</li>
                <li>Lonavala</li>
                <li>Alibaug</li>
                <li>Dubai</li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-playfair text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <Phone size={16} className="text-secondary mt-1 flex-shrink-0" />
                  <span className="text-primary-foreground/70 text-sm">+91 98203 45994</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Mail size={16} className="text-secondary mt-1 flex-shrink-0" />
                  <span className="text-primary-foreground/70 text-sm">rahul@venaraestate.com</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin size={16} className="text-secondary mt-1 flex-shrink-0" />
                  <span className="text-primary-foreground/70 text-sm">Mumbai, Maharashtra</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-secondary/20 mt-12 pt-8 text-center">
            <p className="text-primary-foreground/60 text-sm">
              © {new Date().getFullYear()} Venara Estate. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
